// 心跳接口管理工具
// 直接使用环境变量
import { shouldAutoLogout } from './pageAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

class HeartbeatManager {
  constructor() {
    this.intervalId = null;
    this.isActive = false;
    this.heartbeatInterval = 5 * 60 * 1000; // 5分钟
    this.lastHeartbeat = 0;
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  // 开始心跳
  start() {
    if (this.isActive) {
      console.log('心跳已启动，跳过重复启动');
      return;
    }

    console.log('启动心跳机制，间隔:', this.heartbeatInterval / 1000, '秒');
    this.isActive = true;
    
    // 立即执行一次心跳
    this.performHeartbeat();
    
    // 设置定时器
    this.intervalId = setInterval(() => {
      this.performHeartbeat();
    }, this.heartbeatInterval);
  }

  // 停止心跳
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isActive = false;
    this.retryCount = 0;
    console.log('心跳已停止');
  }

  // 执行心跳请求
  async performHeartbeat() {
    // 检查JWT Token（可选）
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('心跳跳过：未找到JWT Token');
      return;
    }

    // 检查用户是否还在活动（页面可见）
    if (document.hidden) {
      console.log('心跳跳过：页面不可见');
      return;
    }

    try {
      console.log('发送心跳请求...');
      const response = await fetch(`${API_BASE_URL}/health/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.lastHeartbeat = Date.now();
        this.retryCount = 0;
        console.log('心跳成功:', data);
        
        // 更新最后活动时间
        localStorage.setItem('lastActivity', Date.now().toString());
      } else if (response.status === 401 || response.status === 403) {
        // 认证失败，自动退出登录
        console.log('心跳检测到认证失败，自动退出登录');
        this.handleAuthFailure();
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('心跳失败:', error);
      this.retryCount++;
      
      // 如果连续失败超过最大重试次数，停止心跳并检查是否需要退出登录
      if (this.retryCount >= this.maxRetries) {
        console.error('心跳连续失败，停止心跳机制');
        this.stop();
        
        // 检查是否是网络问题还是认证问题
        if (error.message.includes('401') || error.message.includes('403')) {
          this.handleAuthFailure();
        }
      }
    }
  }

  // 处理认证失败
  handleAuthFailure() {
    console.log('处理认证失败，检查当前页面是否需要自动退出');
    
    if (!shouldAutoLogout()) {
      console.log('当前页面不需要自动退出，只停止心跳');
      // 只停止心跳，不清除登录状态
      this.stop();
      return;
    }
    
    console.log('当前页面需要自动退出，执行退出登录');
    
    // 停止心跳
    this.stop();
    
    // 清除所有登录相关的本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('avatar');
    localStorage.removeItem('accountNickName');
    localStorage.removeItem('hasEditAllPermission');
    localStorage.removeItem('lastActivity');
    
    // 清除微信相关数据（可选）
    localStorage.removeItem('wechatUser');
    localStorage.removeItem('wechatToken');
    localStorage.removeItem('isWechatLoggedIn');
    
    // 设置手动退出标识，防止自动授权
    localStorage.setItem('manualLogout', 'true');
    
    // 显示提示信息
    // 直接跳转到登录页，不显示确认弹窗
    window.location.href = '/login';
  }

  // 手动触发心跳
  async triggerHeartbeat() {
    if (!this.isActive) {
      console.log('心跳未启动，无法手动触发');
      return;
    }
    await this.performHeartbeat();
  }

  // 检查心跳状态
  getStatus() {
    return {
      isActive: this.isActive,
      lastHeartbeat: this.lastHeartbeat,
      retryCount: this.retryCount,
      interval: this.heartbeatInterval
    };
  }

  // 设置心跳间隔
  setInterval(interval) {
    this.heartbeatInterval = interval;
    if (this.isActive) {
      // 重新启动心跳以应用新间隔
      this.stop();
      this.start();
    }
  }
}

// 创建全局心跳管理器实例
const heartbeatManager = new HeartbeatManager();

export default heartbeatManager;
